import React, { useEffect, useState, useRef } from 'react';
import { BasicCheckbox, IconLibrary, ThriveIcon } from 'thrive-components';

interface Option  {
    value: string | number, label: string | React.ReactNode, split?:boolean
}

interface SearchFilterMultiSelectButton {
    id: string,
    icon: React.ReactElement,
    options:Option[],
    defaultValues: string[],
    selectedValues: string[],
    name: string,
    setValue:(values: string[]) => void
    isLoading?: boolean
    hasAlwaysOnOptions?:boolean
    
}

const   SearchFilterMultiSelectButton = ({id, icon, options, defaultValues, selectedValues, name, setValue, isLoading=false, hasAlwaysOnOptions=false}: SearchFilterMultiSelectButton) => {
    const dropdownRef = useRef<any>()
    const [selectedOptions, setSelectedOptions] = useState<boolean[]>(options.map((option) => selectedValues.includes(`${option.value}`)));
    const [showDropDown, setShowDropdown] = useState<boolean>(false);
    const [activeElement, setActiveELement] = useState<string>('')
    const [focusIndex, setFocusIndex] = useState<number>(0)
    const [selectedOptionsValues, setUseSelectedOptionsValues] = useState<string[]>(selectedValues);

    useEffect(() => {
        function handleClickOutside(event: Event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            
        };
    }, [])

    useEffect(() => {
        if (!showDropDown) {
            setActiveELement(``)
        }
    }, [showDropDown])

    useEffect(() => {
        setSelectedOptions(options.map((option) => selectedValues.includes(`${option.value}`)))
    }, [selectedValues.length])

    useEffect(() => {
        if(showDropDown) {
            setAndFocusNextElement(getElementName(focusIndex))
        }
    },[focusIndex, showDropDown])

    useEffect(() => {
        const filteredOptions = options.map((el, idx) => {
            if(selectedOptions[idx]) {
                return `${el.value}`
            }
            return '' 
        }).filter(el => el);
        setUseSelectedOptionsValues(filteredOptions)                
    },[selectedOptions])

    useEffect(() => {
        (!isLoading) && setValue(selectedOptionsValues)
    }, [selectedOptionsValues, isLoading])

    function handleKeyPressOnButton(e: any) {
        switch(e.key) {
            case 'ArrowDown':
            case 'ArrowUp': 
            case 'Space':
            case ' ':
            case 'Enter':
            case 'Home':
            case 'End':
                e.preventDefault();
                setShowDropdown(!showDropDown);
                setFocusIndex(e.code === 'End' ? options.length -1 : 0)
                break;
            case 'Tab':
                setShowDropdown(false);
                break;
        }
    }

    function handleKeyPressInList(e: any) {
    
        switch(e.key) {
            case 'Tab':
                e.preventDefault();
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus(); 
                break;
            case 'Enter':
            case 'Space':
            case ' ':
                e.preventDefault();
                toggleCheckbox(focusIndex);
                break;
            case 'ArrowDown':
                e.preventDefault();
                setFocusIndex(focusIndex + 1 >= options.length ? 0 : focusIndex + 1)
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusIndex(focusIndex - 1 < 0  ? options.length -1 : focusIndex - 1)
                break;
            case 'Home':
                e.preventDefault();
                setFocusIndex(0)
                break;
            case 'End':
                e.preventDefault();
                setFocusIndex(options.length -1)
                break;
            case 'Escape':
                setShowDropdown(false);
                document.getElementById(`${id}-open-menu-button`)?.focus();
                break;

        }
    }

    function setAndFocusNextElement(elementId: string){
        if(!elementId) {
            return;
        }

        else if (document.getElementById(elementId)) {
            document.getElementById(elementId)?.focus();
        }
    }

    function toggleCheckbox(index: number) {
        if(hasAlwaysOnOptions) {
            if(!selectedOptions[0] && index === 1) {
                // cannot turn off
                return;
            } 
            else if(!selectedOptions[1] && index === 0) {
                // cannot turn off
                return;
            }          
        }
        setSelectedOptions(selectedOptions.map((el, i) => {
            if (defaultValues.includes(`${options[i].value}`)) {
                return true
            }
            if(index === i ) {
                return !el
            }
            return el
        }))
    }


    function getElementName(index: number) {
        return `${id}-option-${index}`
    }
    
    return (
        <div className='search-drop-down-button-container' ref={dropdownRef}>
            <button 
                id={`${id}-open-menu-button`}
                className='search-dd-button'
                onClick={() => {setShowDropdown(!showDropDown)}}
                role='combobox'
                aria-controls={id}
                aria-owns={id}
                aria-expanded={showDropDown}
                aria-activedescendant={activeElement}
                onKeyDown={(e) => handleKeyPressOnButton(e)}
            >
                {icon}

                <span className='dd-button-label'>
                    {name}
                    {selectedOptionsValues.length > 0 && <span className='selected-count'>{selectedOptionsValues.length}</span>}
                </span>
                
                <ThriveIcon 
                    iconName={IconLibrary.CHEVRON_DOWN}
                    sizeInPx='7'            
                />
            </button>

            {showDropDown && 
                <div 
                    className='search-drop-down-container multi'
                    id={id}
                    role='listbox'
                >
                    {options.map((el, idx) => {
                        return (
                        <>
                            <div 
                                tabIndex={-1}
                                className={`search-dd-option ${activeElement ===  `${id}-option-${idx}` ? 'active' : ''} `}
                                key={`${id}-option-${idx}`}
                                id={`${id}-option-${idx}`}
                                role='option'  
                                onMouseOver={()=>{
                                    setActiveELement(`${id}-option-${idx}`)}
                                }
                                onKeyDown={(e) => {
                                    handleKeyPressInList(e)
                                }}
                                onFocus={() => setActiveELement(`${id}-option-${idx}`)}
                            >
                                <BasicCheckbox 
                                    id={`${id}-checkbox-${idx}`} 
                                    isChecked={selectedOptions[idx]} 
                                    onChange={() => toggleCheckbox(idx)} 
                                    ariaLabel="Select Option"
                                    label={<span>{el.label}{`${defaultValues.includes(`${el.value}`) ? ` (default)`: ''}`}</span>}
                                />
                            </div> 
                            
                            {el.split === true 
                                ? <hr className='search-list-seperator' /> 
                                : null
                            }
                        </>
                    )})}
                </div>
            }
        </div>

    )

}

export default SearchFilterMultiSelectButton;